import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ChevronRight, Copy, Target } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import { Fragment, ReactElement, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { DetailSection, DetailSectionRow } from '@/components/composed';
import { Scrollbar } from '@/components/Scrollbar';
import TemporaryDrawer from '@/components/TemporaryDrawer';
import {
  Badge,
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  KeyValueRow,
  KeyValueTable,
  Skeleton,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
  Tooltip,
} from '@/design-system/components/ui';
import { useMediaQuery } from '@/design-system/hooks/useMediaQuery';
import { maskAddressValue } from '@/utils/format';

import { useProjectsContext } from '../projects/context';
import { ActivityAmountCell } from './components/ActivityAmountCell';
import { translateFlag, usePayoutStatusColors } from './constants';
import { ConversionLog } from './domain';
import { PayoutStatus } from './infra/dtos';
import { logService } from './infra/logService';

interface SearchResultDrawerProps {
  selectedResult: ConversionLog | undefined;
  setSelectedResult: (result: ConversionLog | undefined) => void;
}

export const ConversionDrawer = ({ selectedResult, setSelectedResult }: SearchResultDrawerProps): ReactElement => {
  const { project } = useProjectsContext();
  const isMobile = useMediaQuery('(max-width: 599.95px)');

  const [currentTab, setCurrentTab] = useQueryState('tab', parseAsString.withDefault('conversion'));
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const toggleRow = (id: string): void => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const { data: conversionDetails, isLoading: eventsLoading } = useQuery({
    queryKey: ['conversions-result', { result: selectedResult?.id, project: project?.id }],
    queryFn: async () => {
      return logService.getConversionDetails(project?.id as string, selectedResult?.id as string);
    },
    enabled: !!project && !!selectedResult,
  });

  const payoutStatusColors = usePayoutStatusColors();

  // Skeleton for events table
  const renderEventsSkeleton = (): ReactElement => (
    <Card variant="subcard" overflow="hidden">
      <Scrollbar>
        <Table className="tw:min-w-[600px]">
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-[50px] tw:whitespace-nowrap" />
              <TableHead className="tw:whitespace-nowrap">Date</TableHead>
              <TableHead className="tw:whitespace-nowrap">Event Type</TableHead>
              <TableHead className="tw:whitespace-nowrap">Event Name</TableHead>
              <TableHead className="tw:whitespace-nowrap">User</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell className="tw:whitespace-nowrap">
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>
                <TableCell className="tw:whitespace-nowrap">
                  <Skeleton variant="text" width={120} height={20} />
                </TableCell>
                <TableCell className="tw:whitespace-nowrap">
                  <Skeleton variant="text" width={100} height={20} />
                </TableCell>
                <TableCell className="tw:whitespace-nowrap">
                  <Skeleton variant="text" width={150} height={20} />
                </TableCell>
                <TableCell className="tw:whitespace-nowrap">
                  <Skeleton variant="text" width={140} height={20} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Card>
  );

  return (
    <TemporaryDrawer
      open={!!selectedResult}
      onClose={() => setSelectedResult(undefined)}
      title={selectedResult?.conversion_name ? `Conversion: ${selectedResult.conversion_name}` : 'Conversion'}
      subtitle={
        selectedResult?.date
          ? `Date: ${dayjs(selectedResult.date).format('MMM D HH:mm:ss')} (${dayjs(selectedResult.date).fromNow()})`
          : 'Date: -'
      }
      icon={<Target size={20} />}
    >
      <div className="tw:grid tw:grid-cols-1 tw:gap-4">
        <div>
          <Tabs value={currentTab} onValueChange={setCurrentTab}>
            <TabsList className="tw:border-b tw:border-border tw:w-full">
              <TabsTrigger value="conversion">Conversion Attributes</TabsTrigger>
              <TabsTrigger value="events">
                <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                  <span>Events</span>
                  {eventsLoading ? <Spinner size="sm" /> : <span>({conversionDetails?.events?.length ?? 0})</span>}
                </div>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div>
          {currentTab === 'conversion' && selectedResult && (
            <div className="tw:flex tw:flex-col tw:gap-6">
              <DetailSection title="Details">
                <DetailSectionRow
                  label="Payout status"
                  value={
                    <Badge
                      className="tw:capitalize"
                      style={{
                        backgroundColor:
                          payoutStatusColors[selectedResult.payout_status ?? PayoutStatus.Deferred]?.backgroundColor,
                        color: payoutStatusColors[selectedResult.payout_status ?? PayoutStatus.Deferred]?.color,
                      }}
                    >
                      {selectedResult.payout_status || '-'}
                    </Badge>
                  }
                />

                {selectedResult.flags.length > 0 && (
                  <DetailSectionRow label="Flags" value={selectedResult.flags.map(translateFlag).join(', ')} />
                )}

                {selectedResult.unprocessed_payout_reason && (
                  <DetailSectionRow
                    label="Status details"
                    value={translatePayoutReason(selectedResult.unprocessed_payout_reason)}
                  />
                )}

                {selectedResult.payout_status_details && (
                  <DetailSectionRow label="Payout status details" value={selectedResult.payout_status_details} />
                )}

                <DetailSectionRow label="Source" value={selectedResult.source || '-'} />

                {conversionDetails?.payout_tier_name && (
                  <DetailSectionRow label="Tier" value={conversionDetails.payout_tier_name} />
                )}

                {!!selectedResult.payout_tags?.length && (
                  <DetailSectionRow
                    label="Tags"
                    value={
                      <div className="tw:flex tw:flex-row tw:gap-2">
                        {selectedResult.payout_tags.map((tag) => (
                          <Badge key={tag}>{tag}</Badge>
                        ))}
                      </div>
                    }
                  />
                )}

                {selectedResult.error && <DetailSectionRow label="Error" value={selectedResult.error} />}
              </DetailSection>

              <DetailSection title="Addresses">
                <DetailSectionRow
                  label="End user address"
                  value={
                    selectedResult.end_user_address ? (
                      <span className="tw:inline-flex tw:items-center tw:gap-2 tw:min-w-0">
                        <span className="tw:font-mono tw:truncate">
                          {maskAddressValue(selectedResult.end_user_address)}
                        </span>
                        <CopyToClipboard text={selectedResult.end_user_address}>
                          <Button variant="ghost" size="xs" aria-label="Copy address">
                            <Copy size={12} aria-hidden="true" />
                          </Button>
                        </CopyToClipboard>
                      </span>
                    ) : (
                      '-'
                    )
                  }
                />
                <DetailSectionRow
                  label="Affiliate address"
                  value={
                    selectedResult.affiliate_address ? (
                      <span className="tw:inline-flex tw:items-center tw:gap-2 tw:min-w-0">
                        <span className="tw:font-mono tw:truncate">
                          {maskAddressValue(selectedResult.affiliate_address)}
                        </span>
                        <CopyToClipboard text={selectedResult.affiliate_address}>
                          <Button variant="ghost" size="xs" aria-label="Copy address">
                            <Copy size={12} aria-hidden="true" />
                          </Button>
                        </CopyToClipboard>
                      </span>
                    ) : (
                      '-'
                    )
                  }
                />
              </DetailSection>

              <DetailSection title="Amounts">
                <DetailSectionRow
                  label="End user amount"
                  value={
                    <ActivityAmountCell
                      amount={selectedResult.end_user_amount}
                      paymentCurrency={selectedResult.payment_currency}
                      chainIdentifier={project?.contractChainId ?? undefined}
                    />
                  }
                />
                <DetailSectionRow
                  label="Affiliate amount"
                  value={
                    <ActivityAmountCell
                      amount={selectedResult.affiliate_amount}
                      paymentCurrency={selectedResult.payment_currency}
                      chainIdentifier={project?.contractChainId ?? undefined}
                    />
                  }
                />
              </DetailSection>
            </div>
          )}

          {currentTab === 'events' && (
            <>
              {eventsLoading ? (
                renderEventsSkeleton()
              ) : (
                <Card variant="subcard" overflow="hidden">
                  <Scrollbar>
                    <Table className="tw:min-w-[600px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="tw:w-[50px] tw:whitespace-nowrap" />
                          <TableHead className="tw:whitespace-nowrap">Date</TableHead>
                          <TableHead className="tw:whitespace-nowrap">Event Type</TableHead>
                          <TableHead className="tw:whitespace-nowrap">Event Name</TableHead>
                          <TableHead className="tw:whitespace-nowrap">User</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {conversionDetails?.events?.map((event, index) =>
                          event ? (
                            <Fragment key={index}>
                              <TableRow
                                className="tw:cursor-pointer [&>*]:tw:border-b-0"
                                onClick={() => toggleRow(event.name + index)}
                              >
                                <TableCell className="tw:whitespace-nowrap">
                                  <div
                                    className="tw:transition-transform tw:duration-200"
                                    style={{
                                      transform: expandedRow === event.name + index ? 'rotate(90deg)' : 'none',
                                    }}
                                  >
                                    <ChevronRight />
                                  </div>
                                </TableCell>
                                <TableCell className="tw:whitespace-nowrap">
                                  {event.date ? dayjs.utc(event.date).format('MMM D HH:mm:ss') : '-'}
                                </TableCell>
                                <TableCell className="tw:whitespace-nowrap">
                                  <Badge size="sm" className="tw:capitalize">
                                    {event.type || '-'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="tw:whitespace-nowrap">{event.name || '-'}</TableCell>
                                <TableCell className="tw:whitespace-nowrap tw:break-all">
                                  {isMobile ? (
                                    event.user_address || '-'
                                  ) : event.user_address ? (
                                    <Tooltip content={event.user_address}>
                                      <span>{maskAddressValue(event.user_address)}</span>
                                    </Tooltip>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                              </TableRow>
                              <TableRow
                                style={{
                                  paddingBottom: 0,
                                  paddingTop: 0,
                                }}
                                className="tw:bg-neutral-50 tw:dark:bg-neutral-900"
                              >
                                <TableCell className="tw:w-[50px] tw:p-0 tw:whitespace-nowrap" />
                                <TableCell colSpan={4} className="tw:p-0">
                                  <Collapsible open={expandedRow === event.name + index}>
                                    <CollapsibleContent>
                                      <table className="tw:w-full tw:min-w-[400px] tw:text-sm">
                                        <tbody>
                                          <tr>
                                            <td className="tw:p-4">
                                              <div className="tw:grid tw:grid-cols-12 tw:gap-2">
                                                <div className="tw:col-span-12">
                                                  <Text variant="body-sm" weight="semibold">
                                                    Event Details
                                                  </Text>
                                                </div>
                                                <div className="tw:col-span-6">
                                                  <div className="tw:flex tw:flex-col">
                                                    <Text variant="body-sm" color="muted">
                                                      Trigger
                                                    </Text>
                                                    <Text variant="body-sm">{event.trigger_name || '-'}</Text>
                                                  </div>
                                                </div>
                                                <div className="tw:col-span-6">
                                                  <div className="tw:flex tw:flex-col tw:w-fit">
                                                    <Text variant="body-sm" color="muted">
                                                      User Address
                                                    </Text>
                                                    {isMobile ? (
                                                      <Text variant="body-sm" className="tw:break-all">
                                                        {event.user_address || '-'}
                                                      </Text>
                                                    ) : event.user_address ? (
                                                      <Tooltip content={event.user_address}>
                                                        <Text variant="body-sm">
                                                          {maskAddressValue(event.user_address)}
                                                        </Text>
                                                      </Tooltip>
                                                    ) : (
                                                      <Text variant="body-sm">-</Text>
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="tw:col-span-12">
                                                  <Text variant="body-sm" color="muted" className="tw:mb-2">
                                                    Event Arguments
                                                  </Text>
                                                  {event.args && Object.keys(event.args).length > 0 ? (
                                                    <KeyValueTable>
                                                      {Object.entries(event.args).map(([key, value]) => (
                                                        <KeyValueRow
                                                          key={key}
                                                          label={key}
                                                          value={
                                                            typeof value === 'object'
                                                              ? JSON.stringify(value, null, 2)
                                                              : String(value)
                                                          }
                                                        />
                                                      ))}
                                                    </KeyValueTable>
                                                  ) : (
                                                    <Text variant="body-sm">-</Text>
                                                  )}
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </CollapsibleContent>
                                  </Collapsible>
                                </TableCell>
                              </TableRow>
                            </Fragment>
                          ) : null,
                        )}
                      </TableBody>
                    </Table>
                  </Scrollbar>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </TemporaryDrawer>
  );
};

const payoutReasonLabels: Record<string, string> = {
  enduser_no_tier_found: 'No tier found for end user',
  affiliate_no_tier_found: 'No tier found for affiliate',
  low_balance: 'Project has low balance to process payout',
  attribution_flagged: 'Attribution flagged',
};

const translatePayoutReason = (reason: string): string => {
  return payoutReasonLabels[reason] || reason.replace(/_/g, ' ');
};

// Default export for dynamic imports
export default ConversionDrawer;
